import { useCallback, useEffect, useMemo, useRef, useState, type UIEventHandler } from "react";
import { isNearThreadBottom } from "../messaging/chat-view-state";

export const keepChatViewportVisible = (
  viewport: VisualViewport,
  transcript: HTMLDivElement,
  composer: HTMLDivElement,
  onLayout: () => void,
): (() => void) => {
  const previousMaxHeight = transcript.style.maxHeight;
  const resize = () => {
    // Layout offsets ignore route-entry transforms, which can temporarily put
    // the transcript below the viewport and leave it permanently clamped to zero.
    let transcriptTop = 0;
    for (let element: HTMLElement | null = transcript; element; element = element.offsetParent as HTMLElement | null)
      transcriptTop += element.offsetTop + (element.offsetParent?.clientTop ?? 0);
    for (let ancestor = transcript.parentElement; ancestor; ancestor = ancestor.parentElement)
      transcriptTop -= ancestor.scrollTop;
    const composerHeight = composer.getBoundingClientRect().height;
    transcript.style.maxHeight = `${Math.max(0, viewport.height + viewport.offsetTop - transcriptTop - composerHeight)}px`;
    onLayout();
  };
  resize();
  viewport.addEventListener("resize", resize);
  viewport.addEventListener("scroll", resize);
  const composerObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resize);
  composerObserver?.observe(composer);
  return () => {
    viewport.removeEventListener("resize", resize);
    viewport.removeEventListener("scroll", resize);
    composerObserver?.disconnect();
    transcript.style.maxHeight = previousMaxHeight;
  };
};

export const useChatViewport = (contentKey: unknown, anchorLatestReply = false) => {
  const transcriptRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const followingRef = useRef(true);
  const programmaticScrollRef = useRef(false);
  const [newReply, setNewReply] = useState(false);

  const scrollToBottom = useCallback((smooth = false) => {
    const transcript = transcriptRef.current;
    if (!transcript) return;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    transcript.scrollTo({ top: transcript.scrollHeight, behavior: smooth && !reducedMotion ? "smooth" : "auto" });
  }, []);

  const scrollToLatestReply = useCallback(() => {
    const transcript = transcriptRef.current;
    const replies = transcript?.querySelectorAll<HTMLElement>('[data-testid="chat-assistant-bubble"]');
    const reply = replies?.item((replies?.length ?? 0) - 1);
    if (!transcript || !reply) {
      scrollToBottom();
      return;
    }
    programmaticScrollRef.current = true;
    const transcriptBox = transcript.getBoundingClientRect();
    const replyBox = reply.getBoundingClientRect();
    transcript.scrollTo({
      top: Math.max(0, transcript.scrollTop + replyBox.top - transcriptBox.top),
      behavior: "auto",
    });
    requestAnimationFrame(() => {
      programmaticScrollRef.current = false;
    });
  }, [scrollToBottom]);

  const resumeFollow = useCallback(() => {
    followingRef.current = true;
    setNewReply(false);
    requestAnimationFrame(() => scrollToBottom(true));
  }, [scrollToBottom]);

  const onScroll = useCallback<UIEventHandler<HTMLDivElement>>((event) => {
    if (programmaticScrollRef.current) return;
    const transcript = event.currentTarget;
    const following = isNearThreadBottom(transcript.scrollTop, transcript.clientHeight, transcript.scrollHeight);
    followingRef.current = following;
    if (following) setNewReply(false);
  }, []);

  useEffect(() => {
    if (!transcriptRef.current) return;
    if (!followingRef.current) {
      setNewReply(true);
      return;
    }
    const frame = requestAnimationFrame(() => (anchorLatestReply ? scrollToLatestReply() : scrollToBottom()));
    return () => cancelAnimationFrame(frame);
  }, [anchorLatestReply, contentKey, scrollToBottom, scrollToLatestReply]);

  useEffect(() => {
    const viewport = window.visualViewport;
    const transcript = transcriptRef.current;
    const composer = composerRef.current;
    if (!viewport || !transcript || !composer) return;
    return keepChatViewportVisible(viewport, transcript, composer, () => {
      if (followingRef.current) scrollToBottom();
    });
  }, [contentKey, scrollToBottom]);

  const transcriptProps = useMemo(
    () => ({
      ref: transcriptRef,
      role: "log" as const,
      "aria-live": "polite" as const,
      "aria-relevant": "additions text" as const,
      "aria-atomic": "false" as const,
      "data-lenis-prevent": "true",
      onScroll,
    }),
    [onScroll],
  );

  return { transcriptRef, composerRef, transcriptProps, newReply, resumeFollow };
};
